<script setup>
// Histórico anterior à conexão (Instagram e Messenger): só administradores; abrir a aba
// lê o último andamento sem chamar a Meta, e a importação começa só após a confirmação.
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useLocale } from 'shared/composables/useLocale';
import InboxHealthAPI from 'dashboard/api/inboxHealth';
import Button from 'next/button/Button.vue';
import Dialog from 'next/dialog/Dialog.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  inboxId: { type: [Number, String], required: true },
  network: { type: String, required: true },
});

const POLL_INTERVAL = 5000;
const PREFIX = 'INBOX_MGMT.HISTORY_IMPORT';

const { t, te } = useI18n();
const { isAdmin } = useAdmin();
const { resolvedLocale } = useLocale();

const importState = ref(null);
const loaded = ref(false);
const loadFailed = ref(false);
const isRequesting = ref(false);
const confirmDialog = ref(null);
let pollTimer = null;

const TONES = {
  queued: 'bg-n-blue-3 text-n-blue-11',
  processing: 'bg-n-blue-3 text-n-blue-11',
  completed: 'bg-n-teal-3 text-n-teal-11',
  failed: 'bg-n-ruby-3 text-n-ruby-11',
};

const isActive = computed(() =>
  ['queued', 'processing'].includes(importState.value?.status)
);

const formatDate = value =>
  value
    ? new Intl.DateTimeFormat(resolvedLocale.value, {
        dateStyle: 'short',
        timeStyle: 'short',
      }).format(new Date(value))
    : '';

const statusLabel = computed(() =>
  importState.value
    ? t(`${PREFIX}.STATUS.${importState.value.status.toUpperCase()}`)
    : ''
);

const countsLabel = computed(() =>
  t('INBOX_MGMT.HISTORY_IMPORT.COUNTS', {
    conversations: Number(
      importState.value?.conversations_count || 0
    ).toLocaleString(resolvedLocale.value),
    messages: Number(importState.value?.messages_count || 0).toLocaleString(
      resolvedLocale.value
    ),
  })
);

const whenLabel = computed(() => {
  const current = importState.value;
  if (!current) return '';
  const label = current.finished_at
    ? t('INBOX_MGMT.HISTORY_IMPORT.FINISHED_AT', {
        date: formatDate(current.finished_at),
      })
    : t('INBOX_MGMT.HISTORY_IMPORT.STARTED_AT', {
        date: formatDate(current.started_at || current.created_at),
      });
  return current.requested_by
    ? `${label} ${t('INBOX_MGMT.HISTORY_IMPORT.REQUESTED_BY', { name: current.requested_by })}`
    : label;
});

const errorMessage = computed(() => {
  const code = importState.value?.error_code;
  if (!code) return '';
  const key = `${PREFIX}.ERRORS.${code}`;
  return te(key) ? t(key) : t(`${PREFIX}.ERRORS.unexpected`);
});

const limits = computed(() => [
  t('INBOX_MGMT.HISTORY_IMPORT.LIMIT_MESSAGES'),
  t('INBOX_MGMT.HISTORY_IMPORT.LIMIT_REQUESTS'),
  t('INBOX_MGMT.HISTORY_IMPORT.LIMIT_MEDIA'),
  t('INBOX_MGMT.HISTORY_IMPORT.LIMIT_SAFE'),
]);

const stopPolling = () => {
  window.clearTimeout(pollTimer);
  pollTimer = null;
};

const load = async () => {
  try {
    const { data } = await InboxHealthAPI.getHistoryImport(props.inboxId);
    importState.value = data?.import || null;
    loadFailed.value = false;
  } catch {
    loadFailed.value = true;
  } finally {
    loaded.value = true;
  }
  stopPolling();
  if (isActive.value) pollTimer = window.setTimeout(load, POLL_INTERVAL);
};

const requestImport = async () => {
  if (isRequesting.value) return;
  isRequesting.value = true;
  try {
    const { data } = await InboxHealthAPI.requestHistoryImport(props.inboxId);
    importState.value = data?.import || null;
    confirmDialog.value?.close();
    useAlert(t('INBOX_MGMT.HISTORY_IMPORT.SUCCESS'));
  } catch (error) {
    confirmDialog.value?.close();
    if (error.response?.data?.import) {
      importState.value = error.response.data.import;
    } else {
      useAlert(t('INBOX_MGMT.HISTORY_IMPORT.ERROR'));
    }
  } finally {
    isRequesting.value = false;
    stopPolling();
    if (isActive.value) pollTimer = window.setTimeout(load, POLL_INTERVAL);
  }
};

onMounted(() => {
  if (isAdmin.value) load();
});
onBeforeUnmount(stopPolling);
</script>

<template>
  <section
    v-if="isAdmin"
    class="mx-5 flex max-w-4xl flex-col gap-4 rounded-xl border border-n-weak p-4 sm:mx-6"
    data-test="history-import"
  >
    <div class="flex items-start gap-3">
      <span
        class="flex size-9 shrink-0 items-center justify-center rounded-lg bg-n-alpha-2 text-n-slate-11"
      >
        <Icon icon="i-hugeicons-inbox-download" class="size-4" />
      </span>
      <div class="min-w-0 space-y-1">
        <p class="mb-0 text-body-main font-medium text-n-slate-12">
          {{ t('INBOX_MGMT.HISTORY_IMPORT.TITLE') }}
        </p>
        <p class="mb-0 text-label-small text-n-slate-11">
          {{ t('INBOX_MGMT.HISTORY_IMPORT.DESCRIPTION', { network }) }}
        </p>
      </div>
    </div>

    <div class="rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2.5">
      <p class="mb-1.5 text-label-small font-medium text-n-slate-12">
        {{ t('INBOX_MGMT.HISTORY_IMPORT.LIMITS_TITLE') }}
      </p>
      <ul class="m-0 grid list-none gap-1 p-0">
        <li
          v-for="(limit, index) in limits"
          :key="index"
          class="flex gap-2 text-label-small text-n-slate-11"
        >
          <Icon
            :icon="
              index === limits.length - 1
                ? 'i-hugeicons-security-check'
                : 'i-hugeicons-information-circle'
            "
            class="mt-0.5 size-3.5 shrink-0 text-n-slate-10"
          />
          <span>{{ limit }}</span>
        </li>
      </ul>
    </div>

    <p v-if="loadFailed" class="mb-0 text-label-small text-n-ruby-11">
      {{ t('INBOX_MGMT.HISTORY_IMPORT.LOAD_ERROR') }}
    </p>

    <div
      v-else-if="loaded"
      class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
    >
      <div v-if="importState" class="min-w-0 space-y-1" role="status">
        <div class="flex flex-wrap items-center gap-2">
          <span
            class="inline-flex min-h-6 items-center gap-1.5 rounded-full px-2 py-0.5 text-label-small"
            :class="TONES[importState.status]"
            data-test="history-import-status"
          >
            <span class="size-1.5 rounded-full bg-current" />
            {{ statusLabel }}
          </span>
          <span class="text-label-small text-n-slate-12 tabular-nums">
            {{ countsLabel }}
          </span>
        </div>
        <p class="mb-0 text-label-small text-n-slate-10">{{ whenLabel }}</p>
        <p
          v-if="importState.skipped_count > 0"
          class="mb-0 text-label-small text-n-slate-10"
        >
          {{
            t('INBOX_MGMT.HISTORY_IMPORT.SKIPPED', {
              count: importState.skipped_count,
            })
          }}
        </p>
        <p
          v-if="errorMessage"
          class="mb-0 text-label-small text-n-ruby-11"
          data-test="history-import-error"
        >
          {{ errorMessage }}
        </p>
      </div>
      <p v-else class="mb-0 text-label-small text-n-slate-11">
        {{ t('INBOX_MGMT.HISTORY_IMPORT.NEVER') }}
      </p>
      <Button
        :label="
          importState
            ? t('INBOX_MGMT.HISTORY_IMPORT.BUTTON_AGAIN')
            : t('INBOX_MGMT.HISTORY_IMPORT.BUTTON')
        "
        icon="i-hugeicons-download-04"
        size="sm"
        class="shrink-0"
        :is-loading="isActive"
        :disabled="isActive"
        data-test="history-import-button"
        @click="confirmDialog?.open()"
      />
    </div>

    <Dialog
      ref="confirmDialog"
      type="edit"
      :title="t('INBOX_MGMT.HISTORY_IMPORT.CONFIRM_TITLE')"
      :description="
        t('INBOX_MGMT.HISTORY_IMPORT.CONFIRM_DESCRIPTION', { network })
      "
      :confirm-button-label="t('INBOX_MGMT.HISTORY_IMPORT.CONFIRM')"
      :is-loading="isRequesting"
      @confirm="requestImport"
    />
  </section>
</template>
